import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, IconButton, LinearProgress, Avatar } from '@mui/material';
import { ProfileContext } from '../context/ProfileContext';
import EditIcon from '@mui/icons-material/Edit';
import axiosInstance from '../axiosConfig';
import API_URL from '../config';

const SchoolProfileData = ({ onUpdate }) => {
  const [schoolProfile, setSchoolProfile] = useState(null); // Speichert das Schulprofil
  const [uploadProgress, setUploadProgress] = useState(0); // Upload-Progress
  const [isUploading, setIsUploading] = useState(false); // Indikator für laufenden Upload
  const [isEditing, setIsEditing] = useState(false); // Bearbeitungsmodus
  const { setProfileImage } = React.useContext(ProfileContext);
  const [editedProfile, setEditedProfile] = useState({
    name: '',
    logoUrl: '',
    street: '',
    postalCode: '',
    city: '',
    contactEmail: '',
    contactPhone: '',
  });
  const [schoolVerified, setSchoolVerified] = useState(null);
  const [schoolVerificationDocument, setSchoolVerificationDocument] = useState(null);
  const [managerLength, setManagerLength] = useState(0);
  const [isManagerLengthTooLong, setIsManagerLengthTooLong] = useState(false);

  const handleProfileImageChange = (newImageUrl) => {
    setProfileImage(newImageUrl);
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');

    // Abrufen des Nutzerprofils
    axiosInstance
      .get('/profile/user-profile', {
        headers: {
          Authorization: `${token}`,
        },
      })
      .then((response) => {
        if (response.data.profile) {
          setSchoolVerified(response.data.profile.schoolVerified);
          setSchoolVerificationDocument(response.data.profile.schoolVerificationDocument);
        }
      })
      .catch((error) => {
        console.error('Fehler beim Abrufen des Nutzerprofils:', error);
      });

    // Abrufen des Schulprofils
    axiosInstance
      .get('/profile/school-profile', {
        headers: {
          Authorization: `${token}`,
        },
      })
      .then((response) => {
        if (response.data.profile) {
          setSchoolProfile(response.data.profile);
        }
      })
      .catch((error) => {
        console.error('Fehler beim Abrufen des Schulprofils:', error);
      });
  }, []);

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedProfile(schoolProfile || {});
  };

  const handleSave = () => {
    // Überprüfe, ob der Schulname geändert wurde
    const schoolNameChanged = editedProfile.name !== schoolProfile.name;

    if (schoolVerified && schoolNameChanged) {
      alert('Die Schulbestätigung wurde erfolgreich geprüft. Eine Änderung des Schulnamens ist nicht erlaubt.');
      return; // Speichern verhindern
    }
    const token = localStorage.getItem('authToken');
    axiosInstance
      .post(
        '/profile/school-profile',
        { ...editedProfile },
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      )
      .then((response) => {
        setSchoolProfile(response.data.profile); 
        onUpdate({ type: "saveSuccess", profile: response.data.profile } );
        setIsEditing(false); 
      })
      .catch((error) => {
        console.error('Fehler beim Speichern des Schulprofils:', error);
        onUpdate({ type: "saveError" });
      });
  };

  const handleChange = (event, source) => {
    onUpdate({ type: "editStart" });
    if (source === "textField") {
      const { name, value } = event.target;
  
      if (name === "schoolManager") {
        setManagerLength(value.length);
        setIsManagerLengthTooLong(value.length > 30);
      }
  
      setEditedProfile((prevProfile) => ({
        ...prevProfile,
        [name]: value,
      }));
    } else if (source === "fileInput") {
      handleLogoUpload(event);
    }
  };

  const handleLogoUpload = (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('logo', file);
      const token = localStorage.getItem('authToken');
  
      setIsUploading(true); // Upload beginnt
      axiosInstance.post('/profile/upload-logo', formData, {
        headers: {
          Authorization: `${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted); // Progressbar aktualisieren
        }
      })
      .then((response) => {
        const newLogoUrl = response.data.logoUrl;
        setEditedProfile((prevProfile) => ({
          ...prevProfile,
          logoUrl: newLogoUrl,
        }));
        handleProfileImageChange(newLogoUrl);
        setIsUploading(false);
        setIsEditing(true);
      })
      .catch((error) => {
        console.error('Fehler beim Upload des Logos:', error);
        setIsUploading(false);
        setIsEditing(true);
      });
    }
  };

  return (
    <Box
      sx={{
        maxWidth: '800px',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
    >

      {schoolProfile || isEditing ? (
        <Box
          sx={{
            position: 'relative',
            padding: '1rem',
          }}
        >

          {/* Editier-Button in der rechten oberen Ecke */}
          {!isEditing && (
            <Button
              variant="contained"
              size="small"
              color='primary'
              sx={{
                backgroundColor: 'primary.light',
                color: '#fff',
                position: 'absolute',
                top: '1rem',
                right: '2rem',
                border: 'none',
                '&:hover': {backgroundColor: 'primary.dark', color: '#fff'} // Etwas dunkler beim Hover
              }}
              onClick={handleEditClick}
            >
              Bearbeiten
            </Button>
          )}

          {!isEditing ? (
            // Anzeige des Profils, wenn Daten vorhanden sind und nicht bearbeitet wird
            <Box>
              {[
                { label: 'Schullogo/-flagge (optional)', value: <Avatar alt="Profilbild" src={editedProfile.logoUrl || schoolProfile?.logoUrl} sx={{ width: 100, height: 100, mt: '0.5rem' }} />},
                { label: 'Name der Schule', value: schoolProfile?.name },
                { label: 'Name des Schulleiters', value: schoolProfile?.schoolManager },
                { label: 'Straße', value: schoolProfile?.street },
                { label: 'PLZ', value: schoolProfile?.postalCode },
                { label: 'Ort', value: schoolProfile?.city },
                { label: 'Kontakt E-Mail', value: schoolProfile?.contactEmail },
                { label: 'Kontakt Telefonnummer', value: schoolProfile?.contactPhone },
              ].map((field, index) => (
                <Box key={index} sx={{ marginBottom: '1rem' }}>
                  <Typography
                    variant="subtitle1"
                    color="black"
                    sx={{ fontWeight: 'bold'}}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontStyle: field.value ? 'normal' : 'italic',
                      color: field.value ? 'inherit' : 'gray',
                    }}
                  >
                    {field.value || 'Nicht ausgefüllt'}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            // Bearbeitungsformular
            <Box component="form">
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Schullogo/-flagge (optional)</Typography>
              <Avatar alt="Schullogo/-flagge" src={editedProfile.logoUrl} sx={{ width: 100, height: 100, mb: '1rem', mt: '1rem' }} />
              <input
                type="file"
                onChange={(e) => handleChange(e, "fileInput")}
                accept="image/*"
                style={{ display: 'none' }}
                id="logo-upload"
              />
              {editedProfile.logoUrl && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => setEditedProfile((prevProfile) => ({ ...prevProfile, logoUrl: '' }))}
                  sx={{ mb: '1rem', mr: '1rem', color: 'error.light', '&:hover': { color: 'error.dark', backgroundColor: '#fff' } }}
                >
                  Logo entfernen
                </Button>
              )}
              <label htmlFor="logo-upload">
                <Button variant="outlined" size="small" color="primary" component="span" type="button" sx={{ mb: '1rem', color: 'primary.light', '&:hover': { color: 'primary.dark'}}}>
                  Logo hochladen
                </Button>
              </label>
              {isUploading && (
                <Box sx={{ width: '100%', mt: 2 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </Box>
              )}

              <TextField
                label="Name der Schule"
                name="name"
                value={editedProfile.name}
                onChange={(e) => handleChange(e, "textField")}
                fullWidth
                disabled={schoolVerified}
                helperText={
                  schoolVerified
                    ? 'Die Schulbestätigung wurde erfolgreich geprüft. Eine Änderung ist nicht möglich.'
                    : ''
                }
                margin="normal"
                placeholder="Geben Sie den Namen der Schule ein"
              />

              <TextField
                label="Name des Schulleiters"
                name="schoolManager"
                value={editedProfile.schoolManager}
                onChange={(e) => handleChange(e, "textField")}
                fullWidth
                margin="normal"
                placeholder="Name des Schulleiters"
                helperText={
                  isManagerLengthTooLong
                    ? "Maximale Zeichenanzahl überschritten!"
                    : `Verbleibende Zeichen: ${30 - managerLength}`
                }
                FormHelperTextProps={{
                  style: { color: isManagerLengthTooLong ? "red" : "inherit" }
                }}
              />

              {/* Unterteilung der Adresse in 3 Felder */}
              <TextField
                label="Straße/Hausnummer"
                name="street"
                value={editedProfile.street}
                onChange={(e) => handleChange(e, "textField")}
                fullWidth
                margin="normal"
                placeholder="Straße und Hausnummer"
                sx={{ mt: '2rem'}}
              />
              <TextField
                label="PLZ"
                name="postalCode"
                value={editedProfile.postalCode}
                onChange={(e) => handleChange(e, "textField")}
                fullWidth
                margin="normal"
                placeholder="Postleitzahl"
              />
              <TextField
                label="Ort"
                name="city"
                value={editedProfile.city}
                onChange={(e) => handleChange(e, "textField")}
                fullWidth
                margin="normal"
                placeholder="Ort"
              />

              <TextField
                label="E-Mail"
                name="contactEmail"
                value={editedProfile.contactEmail}
                onChange={(e) => handleChange(e, "textField")}
                fullWidth
                margin="normal"
                placeholder="Geben Sie die Kontakt-E-Mail ein"
              />
              <TextField
                label="Kontakt Telefonnummer"
                name="contactPhone"
                value={editedProfile.contactPhone}
                onChange={(e) => handleChange(e, "textField")}
                fullWidth
                margin="normal"
                placeholder="Geben Sie die Telefonnummer ein"
              />

              <Button variant="contained" size="small" color="primary" onClick={handleSave} sx={{ mt: 2, bgcolor: 'primary.light', '&:hover': { color: '#fff' } }} type="button" disabled={isManagerLengthTooLong}>
                Speichern
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        // Formular für die Platzhalter
        <Box
          sx={{
            border: '1px dashed grey',
            padding: '1rem',
            borderRadius: '8px',
            position: 'relative',
          }}
        >
          <IconButton
            sx={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
            }}
            onClick={handleEditClick}
          >
            <EditIcon />
          </IconButton>

          {/* Platzhalterbild oder Schullogo */}
          <img
            alt="Schullogo"
            src={schoolProfile?.logoUrl}
            style={{
              width: '120px',
              height: '120px',
              position: 'absolute',
              top: '2rem',
              right: '4rem',
              objectFit: 'cover',
              borderRadius: '8px',
            }}
          />

          <Typography variant="h6">Name der Schule</Typography>
          <Typography sx={{ color: 'grey', fontStyle: 'italic' }}>
            Geben Sie den Namen der Schule ein
          </Typography>

          <Typography variant="h6">Name des Schulleiters</Typography>
          <Typography sx={{ color: 'grey', fontStyle: 'italic' }}>
            Geben Sie den Namen des Schulleiters ein
          </Typography>

          <Typography variant="h6">Straße</Typography>
          <Typography sx={{ color: 'grey', fontStyle: 'italic' }}>
            Geben Sie die Straße der Schule ein
          </Typography>

          <Typography variant="h6">PLZ</Typography>
          <Typography sx={{ color: 'grey', fontStyle: 'italic' }}>
            Geben Sie die PLZ der Schule ein
          </Typography>

          <Typography variant="h6">Ort</Typography>
          <Typography sx={{ color: 'grey', fontStyle: 'italic' }}>
            Geben Sie den Ort der Schule ein
          </Typography>

          <Typography variant="h6">E-Mail</Typography>
          <Typography sx={{ color: 'grey', fontStyle: 'italic' }}>
            Geben Sie die E-Mail-Adresse ein
          </Typography>

          <Typography variant="h6">Telefonnummer</Typography>
          <Typography sx={{ color: 'grey', fontStyle: 'italic' }}>
            Geben Sie die Telefonnummer ein
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SchoolProfileData;
